<template>
  <div class="services-component">
    <div class="container-fluid p-0">
      <div class="row g-2">
        <div class="col-sm-12">
          <div class="d-flex align-items-center mb-3">
            <label class="fs-6 text-muted fw-bold mb-0 me-auto">Completed Services</label>
          </div>
          <hr />

          <!-- Display Total Risk Score -->
          <CumulativeRiskScore :entity-id="entity?.id" />

          <div class="row g-0">
            <!-- Left Navigation Panel -->
            <div class="col-md-4 col-lg-3 service-panel-container" :class="{ 'panel-closed': !service_panel_open }"
              v-show="service_panel_open">
              <div class="service-nav-panel">
                <div class="service-search mb-3">
                  <div class="position-relative">
                    <input type="text" class="form-control form-control-sm" placeholder="Search services..."
                      id="service-search-input" v-model="search_query" />
                    <i class="fas fa-search position-absolute search-icon"></i>
                  </div>
                </div>

                <div v-if="services_loading" class="text-muted text-xs px-2 py-1">Loading services...</div>
                <div v-else-if="services_error" class="alert alert-danger py-2 px-3 text-xs">{{ services_error }}
                </div>
                <div v-else-if="!filtered_services.length" class="text-muted text-xs px-2 py-1">No services found.
                </div>

                <div class="service-list">
                  <div v-for="service in filtered_services" :key="service.service_name" class="service-item"
                    :class="{ active: selected_service?.service_name === service.service_name }"
                    @click="select_service(service)">
                    <div class="d-flex align-items-start justify-content-between mb-2">
                      <div class="service-name flex-grow-1">
                        {{ service.service_name }}
                      </div>
                      <div class="service-status ms-2">
                        <i class="fas fa-check-circle text-success"
                          v-if="selected_service?.service_name === service.service_name"></i>
                      </div>
                    </div>
                    <div class="service-description">
                      {{ service.service_description }}
                    </div>
                    <div class="service-type-badge">
                      <span class="badge">{{ format_service_type(service.service_type) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Main Content Area -->
            <div class="col-md-8 col-lg-9 main-content-container" :class="{ 'expanded': !service_panel_open }">
              <div class="main-service-content">
                <div v-if="!selected_service" class="no-service-selected">
                  <div class="text-center text-muted py-5">
                    <div v-if="!service_panel_open" class="mb-3">
                      <i class="fas fa-list me-2"></i>
                      Click the arrow button above to show services
                    </div>
                    <div v-else>
                      <i class="fas fa-arrow-left me-2"></i>
                      Select a service from the left panel to view details
                    </div>
                  </div>
                </div>

                <div v-if="selected_service" class="service-details">
                  <div class="service-header d-flex align-items-center mb-2">
                    <button class="btn btn-bg-secondary shadow-none btn-sm me-3" @click="toggle_service_panel"
                      :title="service_panel_open ? 'Hide Services' : 'Show Services'">
                      <i class="fas" :class="service_panel_open ? 'fa-chevron-left' : 'fa-chevron-right'"></i>
                    </button>
                    <div class="">
                      <label class="fs-5 text-muted fw-bold mb-0">{{ selected_service.service_name }}</label>
                      <p class="text-xs mb-0">{{ selected_service.service_description }}</p>
                    </div>
                  </div>

                  <div class="tab-content mt-3">
                    <WorkflowServiceData :service="selected_service" :entity="entity" :entity-type="entityType" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { validateToken } from "@/utils/auth";

import WorkflowServiceData from "@/layouts/data_hub/components/services/service/WorkflowServiceData.vue";
import CumulativeRiskScore from "@/components/CumulativeRiskScore/CumulativeRiskScore.vue";
import { API_BASE_URL } from "@/config";

export default {
  name: "ServicesAction",
  components: { 
    WorkflowServiceData,
    CumulativeRiskScore,
  },
  props: {
    entity: { type: Object, required: false, default: null },
    entityType: { type: String, required: false, default: null },
  },
  data() {
    return {
      search_query: "",
      selected_service: null,
      service_panel_open: true,
      services: [],
      services_loading: false,
      services_error: null,
    };
  },
  computed: {
    project_id() {
      return this.$store.getters.get_project_id;
    },
    workflow_id() {
      return this.$store.getters.get_workflow_id;
    },
    filtered_services() {
      if (!this.search_query) {
        return this.services;
      }
      return this.services.filter(
        (service) =>
          (service.service_name || '').toLowerCase().includes(this.search_query.toLowerCase()) ||
          (service.service_description || '').toLowerCase().includes(this.search_query.toLowerCase())
      );
    },
  },
  methods: {
    select_service(service) {
      this.selected_service = service;
    },
    toggle_service_panel() {
      this.service_panel_open = !this.service_panel_open;
    },
    format_service_type(type) {
      return type ? type.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()) : '';
    },
    async fetch_services() {
      if (!this.entity || !this.entity.id) {
        this.services = [];
        return;
      }
      this.services_loading = true;
      this.services_error = null;
      try {
        const TOKEN = localStorage.getItem('token');
        if (!TOKEN) throw new Error('Missing auth token');
        const url = `${API_BASE_URL}/verify_api/data_hub/entities/${this.entity.id}/services/completed/`;
        const resp = await fetch(url, { headers: { 'Content-Type': 'application/json', Authorization: `Token ${TOKEN}` } });
        if (!resp.ok) throw new Error(`Failed to fetch services (${resp.status})`);
        const data = await resp.json();
        const incoming = Array.isArray(data?.services) ? data.services : [];
        this.services = incoming.map(s => ({
          id: s.id,
          service_name: s.name,
          service_description: s.description || '',
          service_type: s.type || '',
          workflows: s.workflows || [],
        }));
      } catch (e) {
        console.error('Completed services fetch error', e);
        this.services_error = e.message || String(e);
        this.services = [];
      } finally {
        this.services_loading = false;
      }
    }
  },
  mounted() {
    if (!validateToken(this.$router)) {
      return;
    }
    this.fetch_services();
  },
  watch: {
    entity: {
      immediate: true,
      handler(newVal, oldVal) {
        if ((newVal && newVal.id) !== (oldVal && oldVal?.id)) {
          this.fetch_services();
        }
      }
    }
  }
};
</script>

<style scoped>
.service-panel-container {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.panel-closed {
  width: 0 !important;
  min-width: 0 !important;
  opacity: 0;
}

.main-content-container {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.main-content-container.expanded {
  flex: 0 0 100%;
  max-width: 100%;
}

.service-nav-panel {
  border-right: 1px solid #e9ecef;
  height: 600px;
  overflow-y: auto;
  padding-right: 20px;
  padding-left: 4px;
}

.service-list {
  max-height: 520px;
  overflow-y: auto;
  padding-right: 4px;
}

.service-item {
  padding: 16px;
  border: 2px solid transparent;
  border-radius: 12px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;
}

.service-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.service-item:hover {
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  border-color: var(--primary-color);
}

.service-item:hover::before {
  opacity: 1;
}

.service-item.active {
  border-color: var(--primary-color);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.service-item.active::before {
  opacity: 1;
}

.service-name {
  font-weight: 600;
  font-size: 15px;
  margin-bottom: 6px;
  color: var(--bs-dark, #212529);
  line-height: 1.4;
}

.service-item.active .service-name {
  color: var(--primary-color);
}

.service-description {
  font-size: 13px;
  color: var(--bs-secondary, #6c757d);
  margin-bottom: 10px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-clamp: 2;
  overflow: hidden;
}

.service-type-badge {
  margin-top: 6px;
  display: flex;
  justify-content: flex-start;
}

.service-type-badge .badge {
  font-size: 11px;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: 16px;
  text-transform: capitalize;
  letter-spacing: 0.025em;
  border: 1px solid transparent;
  background: var(--bs-light, #f8f9fa);
  color: var(--bs-dark, #212529);
  transition: all 0.3s ease;
}

.service-item:hover .service-type-badge .badge {
  background: var(--primary-color);
  color: white;
  border-color: var(--primary-color);
}

.service-item.active .service-type-badge .badge {
  background: var(--primary-color);
  color: white;
  border-color: var(--primary-color);
}

.main-service-content {
  padding-left: 20px;
  min-height: 600px;
}

.no-service-selected {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.service-header {
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 5px;
}

.tab-content {
  min-height: 400px;
}

.service-search input {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 10px 12px 10px 40px;
  font-size: 14px;
  transition: all 0.3s ease;
}

.service-search .search-icon {
  top: 50%;
  left: 12px;
  transform: translateY(-50%);
  color: #a0a0a0;
  font-size: 14px;
  pointer-events: none;
}

#service-search-input {
  padding-left: 30px !important;
}

.service-search input:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(var(--primary-color-rgb, 0, 123, 255), 0.1);
  outline: none;
}

.service-search input:focus+.search-icon {
  color: var(--primary-color);
}

.service-search input::placeholder {
  color: #a0a0a0;
  font-style: italic;
}

@media (max-width: 768px) {
  .service-panel-container {
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    z-index: 1050;
    background: white;
    box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
    transform: translateX(-100%);
  }

  .service-panel-container:not(.panel-closed) {
    transform: translateX(0);
  }

  .main-content-container {
    flex: 0 0 100% !important;
    max-width: 100% !important;
  }

  .service-nav-panel {
    border-right: none;
    border-bottom: 1px solid #e9ecef;
    height: 100vh;
    max-height: none;
    padding-right: 15px;
    padding-left: 15px;
    margin-bottom: 0;
  }

  .service-list {
    max-height: calc(100vh - 120px);
  }

  .main-service-content {
    padding-left: 0;
  }

  .service-item {
    margin-bottom: 8px;
    padding: 12px;
  }
}
</style>
